import { getUser } from "@/api/user";
import { useStaticAuth } from "@/contexts/StaticAuthProvider";
import { NewUser, UserProfile } from "@/types/ApiResponses/User";
import { CacheUpdater, createRecordOps } from "@/utils/queryCache";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

const userQueryKeys = {
  account: "user-account",
};

export const useUserQuery = <TData = NewUser>(
  select?: (data: NewUser) => TData
) => {
  const authData = useStaticAuth();
  return useQuery({
    queryKey: [userQueryKeys.account],
    queryFn: getUser,
    initialData: authData.user,
    select,
  });
};

export const getUserProfile = (user: NewUser): UserProfile => {
  return {
    image: user.image,
  };
};

export const useUser = (): NewUser => {
  const query = useUserQuery();
  return query.data;
};

export const useUserProfile = (): UserProfile => {
  const query = useUserQuery(getUserProfile);
  return query.data;
};

interface UserManager {
  get: () => NewUser | undefined;
  replace: (data: NewUser) => void;
  patch: (updater: CacheUpdater<NewUser>) => void;
  invalidate: () => void;
}

export const createUserManager = (queryClient: QueryClient): UserManager => {
  const ops = createRecordOps<NewUser>(queryClient, [userQueryKeys.account]);
  return {
    get: () => ops.get(),
    replace: (data) => ops.replace(data),
    patch: (updater) => ops.patch(updater),
    invalidate: () => ops.invalidate(),
  };
};

export const useUserManager = (): UserManager => {
  const queryClient = useQueryClient();
  return useMemo(() => createUserManager(queryClient), [queryClient]);
};
